import { useState } from "react";
import { Link } from "react-router-dom";
import { SetPasswordForm } from "@/components/SetPasswordForm";
import type { AuthedRequester } from "@/lib/brokerAuth";

import { buttonClass } from "@/components/buttonStyles";
// Account-security Password card (BRK-S026). Always lets the broker manage their
// password from inside the app: SET one when they have none (an invite-only
// broker who's only ever used email-OTP), or CHANGE an existing one. The emailed
// reset (/forgot-password) stays available too — it's the locked-out path and
// works even if a password was never set. After a successful set/change the
// parent re-reads the posture (onChanged) so posture-driven UI stays in sync.
export function PasswordCard({
  request,
  hasPassword,
  onChanged,
}: {
  request: AuthedRequester;
  hasPassword: boolean;
  onChanged: () => void;
}) {
  const [editing, setEditing] = useState(false);
  const [done, setDone] = useState(false);

  async function onSuccess() {
    setEditing(false);
    setDone(true);
    onChanged();
  }

  return (
    <div className="flex flex-col gap-4 rounded-lg border border-brk-border bg-brk-panel p-5">
      <div className="flex flex-col gap-1">
        <h2 className="text-heading">Password</h2>
        <p className="text-sm text-brk-dim">
          {hasPassword
            ? "Change your password, or reset it by email if you’ve forgotten it."
            : "You don’t have a password yet. Set one to sign in with your email and password, or to use an authenticator app."}
        </p>
      </div>

      {done && !editing && (
        <p
          role="status"
          className="text-sm font-medium text-green-700 dark:text-green-300"
        >
          ✓ Password {hasPassword ? "changed" : "set"}.
        </p>
      )}

      {editing ? (
        <div className="flex flex-col gap-4">
          <SetPasswordForm
            mode={hasPassword ? "change" : "set"}
            request={request}
            onSuccess={onSuccess}
          />
          <button
            type="button"
            onClick={() => setEditing(false)}
            className="self-start text-sm text-brk-accent hover:underline"
          >
            Cancel
          </button>
        </div>
      ) : (
        <div className="flex flex-wrap items-center gap-4">
          <button
            type="button"
            onClick={() => {
              setEditing(true);
              setDone(false);
            }}
            className={`${buttonClass()} self-start`}
          >
            {hasPassword ? "Change password" : "Set a password"}
          </button>
          {hasPassword && (
            <Link
              to="/forgot-password"
              className="text-sm text-brk-accent hover:underline"
            >
              Reset via email
            </Link>
          )}
        </div>
      )}
    </div>
  );
}
